const MAPS_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";

let mapsLoaderInflight: Promise<void> | null = null;

/**
 * Loads the Google Maps JavaScript API exactly once per session (web-only).
 * Always includes the `places` library so both map views and AddressQuestion
 * share a single <script> tag, preventing the "included multiple times" console
 * error that was being captured as a Sentry event from every active session.
 *
 * Previously, TravelMap and the Explore-tab map each appended their own script
 * tag without checking whether AddressQuestion had already kicked off a load
 * with `&libraries=places`. The result was two concurrent loads — one bare and
 * one with places — which Google Maps detects and rejects with a warning.
 */
export function ensureMapsLoaded(): Promise<void> {
    if (typeof window === "undefined" || !MAPS_KEY) {
        return Promise.resolve();
    }
    const win = window as unknown as { google?: { maps?: unknown } };
    if (win.google?.maps) {
        return Promise.resolve();
    }
    if (mapsLoaderInflight) {
        return mapsLoaderInflight;
    }
    mapsLoaderInflight = new Promise<void>((resolve) => {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.head.appendChild(script);
    });
    return mapsLoaderInflight;
}

/** Resets the singleton — for use in tests only. */
export function _resetMapsLoaderForTesting(): void {
    mapsLoaderInflight = null;
}
